import React, { useState } from 'react';
import { useGetIdentity, useLogout } from '@refinedev/core';
import {
  Layout,
  Space,
  Dropdown,
  Avatar,
  Typography,
  theme,
  Grid,
  Modal,
  Form,
  Input,
  message,
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const Header: React.FC = () => {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const { data: user } = useGetIdentity<{
    id: string;
    name: string;
    email: string;
    avatar?: string;
    role: string;
  }>();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();

  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm();

  const menuItems: MenuProps['items'] = [
    {
      key: 'my-permissions',
      icon: <SafetyCertificateOutlined />,
      label: 'My Permissions',
    },
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: 'Change Password',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      logout();
    } else if (key === 'change-password') {
      setPasswordModalVisible(true);
    } else if (key === 'my-permissions') {
      navigate('/profile/permissions');
    }
  };

  const handleChangePassword = async (values: { currentPassword: string; newPassword: string }) => {
    setPasswordLoading(true);
    try {
      await adminApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success('Password changed successfully');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch {
      message.error('Failed to change password. Check your current password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <>
      <Layout.Header
        style={{
          backgroundColor: token.colorBgContainer,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: screens.xs ? '0 12px' : '0 24px',
          height: 64,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Space size="middle">
          <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} placement="bottomLeft">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar
                src={user?.avatar}
                icon={!user?.avatar && <UserOutlined />}
                style={{ backgroundColor: token.colorPrimary }}
              />
              {screens.sm && (
                <Space direction="vertical" size={0} style={{ lineHeight: '1.2' }}>
                  <Text strong>{user?.name || 'Admin'}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {user?.role?.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </Text>
                </Space>
              )}
            </Space>
          </Dropdown>
        </Space>
      </Layout.Header>

      <Modal
        title="Change Password"
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        onOk={() => passwordForm.submit()}
        confirmLoading={passwordLoading}
        okText="Change Password"
      >
        <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: 'Enter current password' }]}
          >
            <Input.Password placeholder="Current password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Enter new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password placeholder="New password (min 8 characters)" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
